/**
 * Dev/test modal for rating a searched itinerary via long-press on the itinerary list.
 */

import React, { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { itineraryReviewService } from "../../services/ItineraryReviewService";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { type ItineraryDTO } from "../../types/dtos/api/itinerary/itinerary.dto";

interface ItineraryReviewModalProps {
    "isOpen":    boolean;
    "itinerary": ItineraryDTO | null;
    "isDarkUi":  boolean;
    "onClose":   () => void;
    "onSaved":   () => void;
    "onError":   ( message: string ) => void;
}

const STAR_VALUES = [ 1, 2, 3, 4, 5 ] as const;

interface ItineraryReviewModalContentProps {
    "itinerary": ItineraryDTO;
    "isDarkUi":  boolean;
    "onClose":   () => void;
    "onSaved":   () => void;
    "onError":   ( message: string ) => void;
}

/**
 * Inner modal content with isolated form state per open cycle.
 */
const ItineraryReviewModalContent: React.FC<ItineraryReviewModalContentProps> = ( {
    itinerary,
    isDarkUi,
    onClose,
    onSaved,
    onError,
} ) => {
    const [ selectedNote, setSelectedNote ] = useState<number>( 0 );
    const [ comment, setComment ] = useState<string>( "" );
    const [ isSaving, setIsSaving ] = useState<boolean>( false );
    const palette = getUIColorPalette( isDarkUi );

    const handleBackdropClick = useCallback( ( event: React.MouseEvent<HTMLDivElement> ) => {
        event.stopPropagation();
        onClose();
    }, [ onClose ] );

    const handleDialogClick = useCallback( ( event: React.MouseEvent<HTMLDivElement> ) => {
        event.stopPropagation();
    }, [] );

    const handleCancelClick = useCallback( ( event: React.MouseEvent<HTMLButtonElement> ) => {
        event.stopPropagation();
        onClose();
    }, [ onClose ] );

    const handleSaveClick = useCallback( ( event: React.MouseEvent<HTMLButtonElement> ) => {
        event.stopPropagation();

        if ( selectedNote < 1 || isSaving ) {
            return;
        }

        setIsSaving( true );
        void itineraryReviewService.createReview( {
            "note":      selectedNote,
            "itinerary": itinerary,
            "comment":   comment.trim(),
        } ).then( () => {
            onSaved();
        } ).
            catch( () => {
                onError( "Impossible d'enregistrer la review. Réessayez." );
            } ).
            finally( () => {
                setIsSaving( false );
            } );
    }, [ comment, isSaving, itinerary, onError, onSaved, selectedNote ] );

    return createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            onClick={handleBackdropClick}
            role="presentation"
            style={{ "zIndex": 999999 }}
        >
            <div
                className="rounded-lg p-6 m-4 max-w-md w-full shadow-xl"
                onClick={handleDialogClick}
                role="dialog"
                style={{
                    "backgroundColor": palette.modalCardBackground,
                    "color":           palette.modalCardTextPrimary,
                    "border":          `1px solid ${ palette.modalCardBorder }`,
                }}
            >
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">
                        Noter l&apos;itinéraire
                    </h2>
                    <button
                        className="text-2xl font-bold"
                        onClick={handleCancelClick}
                        style={{ "color": palette.overlayTextSecondary }}
                        type="button"
                    >
                        ×
                    </button>
                </div>
                <div className="mb-4">
                    <span
                        className="block text-sm font-medium mb-2"
                        style={{ "color": palette.overlayTextSecondary }}
                    >
                        Note
                    </span>
                    <div className="flex gap-2">
                        {STAR_VALUES.map( ( starValue ) => {
                            const isSelected = starValue <= selectedNote;

                            return (
                                <button
                                    aria-label={`${ starValue } étoile${ starValue > 1 ? "s" : "" }`}
                                    className="text-3xl leading-none"
                                    key={starValue}
                                    onClick={() => {
                                        setSelectedNote( starValue );
                                    }}
                                    style={{
                                        "color": isSelected
                                            ? "var(--itinerary-brand-accent, #1bd98d)"
                                            : palette.overlayTextSecondary,
                                    }}
                                    type="button"
                                >
                                    {isSelected ? "★" : "☆"}
                                </button>
                            );
                        } )}
                    </div>
                </div>
                <div className="mb-6">
                    <label
                        className="block text-sm font-medium mb-2"
                        htmlFor="itinerary-review-comment"
                        style={{ "color": palette.overlayTextSecondary }}
                    >
                        Commentaire
                    </label>
                    <textarea
                        className="w-full min-h-[96px] rounded-lg border px-3 py-2 text-sm"
                        id="itinerary-review-comment"
                        onChange={( event ) => {
                            setComment( event.target.value );
                        }}
                        placeholder="Votre commentaire (optionnel)"
                        style={{
                            "borderColor":     palette.overlayBorder,
                            "backgroundColor": palette.overlayCardBackgroundSoft,
                            "color":           palette.overlayTextPrimary,
                        }}
                        value={comment}
                    />
                </div>
                <div className="flex justify-end gap-3">
                    <button
                        className="px-4 py-2 rounded-lg border text-sm font-medium"
                        onClick={handleCancelClick}
                        style={{
                            "borderColor": palette.overlayBorder,
                            "color":       palette.overlayTextSecondary,
                        }}
                        type="button"
                    >
                        Annuler
                    </button>
                    <button
                        className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                        disabled={selectedNote < 1 || isSaving}
                        onClick={handleSaveClick}
                        style={{
                            "backgroundColor": "var(--itinerary-brand-accent, #1bd98d)",
                            "color":           "#ffffff",
                        }}
                        type="button"
                    >
                        {isSaving ? "Enregistrement..." : "Enregistrer"}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

const ItineraryReviewModal: React.FC<ItineraryReviewModalProps> = ( {
    isOpen,
    itinerary,
    isDarkUi,
    onClose,
    onSaved,
    onError,
} ) => {
    if ( !isOpen || itinerary === null ) {
        return null;
    }

    return (
        <ItineraryReviewModalContent
            isDarkUi={isDarkUi}
            itinerary={itinerary}
            onClose={onClose}
            onError={onError}
            onSaved={onSaved}
        />
    );
};

export default ItineraryReviewModal;
